import * as React from "react";
import { Button } from "@eduviaos/ui-kit";
import { Sparkles } from "lucide-react";

/** Variant / size props derived from the ui-kit `Button` (its `ButtonProps`
 *  interface is not re-exported from the package barrel). */
type ButtonOwnProps = React.ComponentProps<typeof Button>;

/**
 * AI-assist trigger button (Phase F-finale-4 Slice 6-FE).
 *
 * A thin, reusable trigger that opens the {@link AIAssistDrawer}. It composes
 * the ui-kit `Button` (which already carries the Emil-standard press feedback —
 * `active:scale-[0.985]`, `transition … duration-150 ease-out`) so we add no
 * bespoke animation: a low-frequency authoring affordance gets the platform's
 * standard button motion, nothing louder.
 *
 * Two presentations:
 *   - text  (default): sparkles icon + visible label — used for the headline
 *     funnel-level trigger.
 *   - `iconOnly`: compact sparkles-only button whose accessible name comes from
 *     `label` (rendered both as `aria-label` and the native `title` tooltip) —
 *     used inline on dense stage / requirement rows so the button never steals
 *     horizontal space from the row's primary controls.
 */
export interface PromptInputButtonProps {
  /** Open the AI assist drawer for this scope. */
  onClick: () => void;
  /**
   * Accessible name + (for the text presentation) visible label.
   * Defaults to `"AI assist"`.
   */
  label?: string;
  /** Render a compact, icon-only trigger (label becomes the accessible name). */
  iconOnly?: boolean;
  /** Visual variant — defaults to `"outline"` so the trigger reads as secondary. */
  variant?: ButtonOwnProps["variant"];
  /** Size — defaults to `"sm"` (text) or `"icon"` (icon-only). */
  size?: ButtonOwnProps["size"];
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function PromptInputButton({
  onClick,
  label = "AI assist",
  iconOnly = false,
  variant = "outline",
  size,
  disabled,
  className,
  "data-testid": testId,
}: PromptInputButtonProps) {
  const resolvedSize = size ?? (iconOnly ? "icon" : "sm");

  return (
    <Button
      type="button"
      variant={variant}
      size={resolvedSize}
      onClick={onClick}
      disabled={disabled}
      className={className}
      data-testid={testId}
      aria-label={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
    >
      <Sparkles aria-hidden />
      {iconOnly ? null : label}
    </Button>
  );
}
